import { Bars3Icon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Badge } from '@redfoxwp/core-components/components/badge';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { DragHandle } from '@redfoxwp/core-components/components/sortable';
import { Toggle } from '@redfoxwp/core-components/components/toggle';
import { __ } from '@wordpress/i18n';
import React, { useState } from 'react';

import { StyledTabTitle } from './styled';
import type { TabItemRendererProps } from './types';

const actionButtonStyleProps = {
  backgroundColor: 'transparent',
  color: colors.gray[800],
  borderWidth: '0',
  borderRadius: '5px',
  width: '32px',
  height: '32px',
  padding: '0',
  hoverBoxShadow: `0px 4px 10px ${colors.primary[100]}`,
  hoverBackgroundColor: 'transparent',
  hoverColor: colors.primary[300],
};

const deleteButtonStyleProps = {
  ...actionButtonStyleProps,
  hoverColor: colors.error,
};

export const TabItemRenderer: React.FC<TabItemRendererProps> = ({
  tab,
  isDragging,
  onEdit,
  onDelete,
  onToggleEnabled,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleEdit = () => {
    onEdit(tab);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(tab.id);
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleToggleEnabled = (checked: boolean) => {
    onToggleEnabled(tab.id, checked);
  };

  // Create localized strings for the modal
  const modalStrings: ModalStrings = {
    closeModal: __('Close modal', 'redfox-tab-manager'),
  };

  return (
    <>
      <Card
        styleProps={{
          padding: '10px',
          radius: '5px',
          background: '#fff',
          border: `1px solid ${isDragging ? colors.primary[100] : colors.gray[100]}`,
        }}
      >
        <Flex direction='row' justify='space-between' align='center'>
          <Flex direction='row' align='center' gap='10px'>
            <DragHandle title={__('Drag to reorder', 'redfox-tab-manager')}>
              <Bars3Icon width={24} height={24} />
            </DragHandle>
            <Flex direction='row' gap='10px'>
              <StyledTabTitle>{tab.title}</StyledTabTitle>
              {tab.type && (
                <Badge
                  size='small'
                  variant='solid'
                  styleProps={(() => {
                    const baseStyle = {
                      fontSize: '11px',
                      fontWeight: '500',
                      padding: '2px 8px',
                      borderRadius: '12px',
                    };

                    if (tab.type === 'woocommerce_default') {
                      return {
                        ...baseStyle,
                        backgroundColor: '#eee3ff',
                        color: '#873eff',
                        borderColor: '#d0b2ff',
                      };
                    } else if (tab.type === 'redfoxwp_tab') {
                      return {
                        ...baseStyle,
                        backgroundColor: colors.primary[50],
                        color: colors.primary[300],
                        borderColor: colors.primary[100],
                      };
                    } else {
                      return {
                        ...baseStyle,
                        backgroundColor: colors.gray[100],
                        color: colors.gray[600],
                        borderColor: colors.gray[200],
                      };
                    }
                  })()}
                >
                  {(() => {
                    if (tab.type === 'woocommerce_default') {
                      return 'WooCommerce';
                    } else if (tab.type === 'redfoxwp_tab') {
                      return 'RedFox WP';
                    }
                    return 'Custom';
                  })()}
                </Badge>
              )}
            </Flex>
          </Flex>
          <Flex direction='row' align='center' gap='10px'>
            {tab.type !== 'woocommerce_default' && (
              <Toggle checked={tab.enabled} onChange={handleToggleEnabled} size='small' />
            )}

            {tab.type !== 'woocommerce_default' && (
              <Button variant='ghost' onClick={handleEdit} styleProps={actionButtonStyleProps}>
                <PencilIcon width={16} height={16} />
              </Button>
            )}

            {tab.type !== 'woocommerce_default' && (
              <Button
                variant='ghost'
                onClick={handleDeleteClick}
                styleProps={deleteButtonStyleProps}
              >
                <TrashIcon width={16} height={16} />
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title={__('Confirm Delete', 'redfox-tab-manager')}
        strings={modalStrings}
        styleProps={{
          maxWidth: '400px',
          borderRadius: '8px',
        }}
        footer={
          <Flex gap='12px' justify='flex-end'>
            <Button variant='outline' onClick={handleDeleteCancel}>
              {__('Cancel', 'redfox-tab-manager')}
            </Button>
            <Button
              variant='solid'
              onClick={handleDeleteConfirm}
              styleProps={{
                backgroundColor: colors.error,
                hoverBackgroundColor: colors.primary[600],
                color: '#fff',
              }}
            >
              {__('Delete', 'redfox-tab-manager')}
            </Button>
          </Flex>
        }
      >
        <p style={{ margin: '0 0 16px 0', fontSize: '14px', lineHeight: '1.5', color: '#1e1e1e' }}>
          {__(
            'Are you sure you want to delete this tab? This action cannot be undone.',
            'redfox-tab-manager'
          )}
        </p>
      </Modal>
    </>
  );
};
